import { createSlice, PayloadAction } from "@reduxjs/toolkit";
import { RootState } from "@core/app/store/store";
import dayjs from "dayjs";

interface CalendarState {
  asideWidth: number;
  view: string;
  showWeekends: boolean;
  choosedDate: string;
}

interface StoredCalendarState extends CalendarState {
  viewSavedDate: string;
}

const CALENDAR_STORAGE_KEY = "calendarState";

const loadStateFromLocalStorage = (): Partial<CalendarState> => {
  const storedState = localStorage.getItem(CALENDAR_STORAGE_KEY);

  const defaults = {
    asideWidth: 0,
    view: "month",
    showWeekends: true,
    choosedDate: dayjs().toISOString(),
  };

  if (!storedState) {
    return defaults;
  }

  try {
    const parsedState: StoredCalendarState = JSON.parse(storedState);

    if (parsedState.viewSavedDate) {
      const savedDate = dayjs(parsedState.viewSavedDate);
      const currentDate = dayjs();

      if (!savedDate.isSame(currentDate, "month")) {
        localStorage.removeItem(CALENDAR_STORAGE_KEY);

        return defaults;
      }
    }

    return {
      asideWidth: parsedState.asideWidth ?? defaults.asideWidth,
      view: parsedState.view ?? defaults.view,
      showWeekends: parsedState.showWeekends ?? defaults.showWeekends,
      choosedDate: parsedState.choosedDate ?? defaults.choosedDate,
    };
  } catch (error) {
    console.error("❌ Failed to parse calendar state from localStorage", error);
    localStorage.removeItem(CALENDAR_STORAGE_KEY);
    return defaults;
  }
};

const saveStateToLocalStorage = (state: CalendarState, viewChanged: boolean = false) => {
  let viewSavedDate = dayjs().toISOString();
  if (!viewChanged) {
    try {
      const storedState = localStorage.getItem(CALENDAR_STORAGE_KEY);
      if (storedState) {
        const parsedState: StoredCalendarState = JSON.parse(storedState);
        viewSavedDate = parsedState.viewSavedDate || dayjs().toISOString();
      }
    } catch {
      viewSavedDate = dayjs().toISOString();
    }
  }

  const stateToSave: StoredCalendarState = {
    ...state,
    viewSavedDate,
  };

  try {
    localStorage.setItem(CALENDAR_STORAGE_KEY, JSON.stringify(stateToSave));
  } catch (error) {
    console.error("❌ Failed to save calendar state to localStorage", error);
  }
};

const initialState: CalendarState = {
  asideWidth: 0,
  view: "month",
  showWeekends: true,
  choosedDate: dayjs().toISOString(),
  ...loadStateFromLocalStorage(),
};

const calendarSlice = createSlice({
  name: "calendar",
  initialState,
  reducers: {
    setAsideWidth(state, action: PayloadAction<number>) {
      state.asideWidth = action.payload;
      saveStateToLocalStorage(state);
    },
    storeView(state, action: PayloadAction<string>) {
      state.view = action.payload;
      saveStateToLocalStorage(state, true); // true indicates view was changed
    },
    setShowWeekends(state, action: PayloadAction<boolean>) {
      state.showWeekends = action.payload;
      saveStateToLocalStorage(state);
    },
    setChoosedDate(state, action: PayloadAction<string>) {
      state.choosedDate = action.payload;
      saveStateToLocalStorage(state);
    },
  },
});

export const {
  setAsideWidth,
  storeView,
  setShowWeekends,
  setChoosedDate,
} = calendarSlice.actions;

export const selectCalendarAsideWidth = (state: RootState) =>
  state.calendar.asideWidth;

export const selectCalendarView = (state: RootState) =>
  state.calendar.view;

export const selectCalendarShowWeekends = (state: RootState) =>
  state.calendar.showWeekends;

export const selectCalendarChoosedDate = (state: RootState) =>
  state.calendar.choosedDate;

export default calendarSlice.reducer;
